import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import type { SendInvitationOutreachEmailParams } from '@/hooks/actions/use-send-invitation-outreach-email';
import { useSendInvitationOutreachEmail } from '@/hooks/actions/use-send-invitation-outreach-email';
import { invitationOutreachPusherService } from '@/services/invitation-outreach-pusher-service';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SendCallbacks {
  onComplete?: () => void;
  onQueued?: () => void;
}

/**
 * Upper bound on how long the composer stays pending on a realtime result. A lost
 * terminal event would otherwise pin it in "sending" forever; past this point we
 * fall back to the queued behaviour (the worker keeps going server-side).
 */
const SEND_RESULT_TIMEOUT_MS = 60_000;

/**
 * Shared invitation-outreach send rail. It subscribes before enqueueing, keeps
 * the UI pending until the worker reports sent/failed, and degrades to the
 * historical queued-and-close behaviour when realtime is unavailable.
 */
export function useTrackedInvitationOutreach() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const mutation = useSendInvitationOutreachEmail();
  const [isAwaitingResult, setIsAwaitingResult] = useState(false);
  const invitationIdRef = useRef<string | null>(null);
  const outreachIdRef = useRef<string | null>(null);
  const terminalEventReceivedRef = useRef(false);
  const callbacksRef = useRef<SendCallbacks>({});
  const resultTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The pusher service is a singleton shared with the email composer. Releasing
  // only OUR registration keeps the other surface's tracking alive.
  const subscriptionIdRef = useRef<string | null>(null);

  const clearResultTimeout = useCallback(() => {
    if (resultTimeoutRef.current !== null) {
      clearTimeout(resultTimeoutRef.current);
      resultTimeoutRef.current = null;
    }
  }, []);

  const releaseSubscription = useCallback(() => {
    if (subscriptionIdRef.current === null) return;
    invitationOutreachPusherService.unsubscribe(subscriptionIdRef.current);
    subscriptionIdRef.current = null;
  }, []);

  const clearSubscription = useCallback(() => {
    invitationIdRef.current = null;
    outreachIdRef.current = null;
    clearResultTimeout();
    setIsAwaitingResult(false);
    releaseSubscription();
  }, [clearResultTimeout, releaseSubscription]);

  const announceStillSending = useCallback(() => {
    toaster.create({
      title: t('invitationOutreach.send.queued.title'),
      description: t('invitationOutreach.send.queued.description'),
      type: 'success',
    });
    const onQueued = callbacksRef.current.onQueued;
    clearSubscription();
    onQueued?.();
  }, [clearSubscription, t]);

  useEffect(
    () => () => {
      if (resultTimeoutRef.current !== null) clearTimeout(resultTimeoutRef.current);
      if (subscriptionIdRef.current !== null) {
        invitationOutreachPusherService.unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    },
    []
  );

  const send = useCallback(
    async (params: SendInvitationOutreachEmailParams, callbacks: SendCallbacks = {}) => {
      invitationIdRef.current = params.invitationId;
      outreachIdRef.current = null;
      terminalEventReceivedRef.current = false;
      callbacksRef.current = callbacks;
      let didSubscribe = false;

      // Subscribing can take seconds when realtime is degraded. Enter the pending
      // state up front so the Send button is disabled for that whole window,
      // otherwise a second click ships a duplicate POST and a duplicate email.
      clearResultTimeout();
      setIsAwaitingResult(true);
      resultTimeoutRef.current = setTimeout(() => {
        resultTimeoutRef.current = null;
        if (terminalEventReceivedRef.current) return;
        announceStillSending();
      }, SEND_RESULT_TIMEOUT_MS);

      const isCurrentEvent = (data: { outreachId: string; invitationId: string }) =>
        data.invitationId === invitationIdRef.current &&
        (!outreachIdRef.current || data.outreachId === outreachIdRef.current);

      const brandId = user?.currentBrand?.id;
      if (brandId) {
        try {
          const subscriptionId = await invitationOutreachPusherService.subscribe(brandId, {
            onComplete: (data) => {
              if (!isCurrentEvent(data)) return;
              terminalEventReceivedRef.current = true;
              queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
              toaster.create({
                title: t('invitationOutreach.events.complete.title'),
                description: t('invitationOutreach.events.complete.description'),
                type: 'success',
              });
              const onComplete = callbacksRef.current.onComplete;
              clearSubscription();
              onComplete?.();
            },
            onFailed: (data) => {
              if (!isCurrentEvent(data)) return;
              terminalEventReceivedRef.current = true;
              queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
              const translated = data.errorCode
                ? t(`errors.${data.errorCode}`, { defaultValue: '' })
                : '';
              toaster.create({
                title: t('invitationOutreach.events.failed.title'),
                description:
                  translated ||
                  data.errorMessage ||
                  t('invitationOutreach.events.failed.description'),
                type: 'error',
                duration: 6000,
              });
              clearSubscription();
            },
            onUnknown: (data) => {
              if (!isCurrentEvent(data)) return;
              terminalEventReceivedRef.current = true;
              queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
              toaster.create({
                title: t('invitationOutreach.events.unknown.title'),
                description: t('invitationOutreach.events.unknown.description'),
                type: 'warning',
                duration: 8000,
              });
              // Provider outcome is ambiguous: never retry implicitly, because a
              // retry could create a duplicate message. The invitation timeline
              // now carries the terminal `unknown` state for a deliberate review.
              clearSubscription();
            },
          });
          subscriptionIdRef.current = subscriptionId;
          // A terminal event can land between the channel bind and this
          // assignment: its handler had no id to release, so release it here.
          if (terminalEventReceivedRef.current) {
            releaseSubscription();
          }
          didSubscribe = true;
        } catch (error) {
          console.warn('[InvitationOutreach] Pusher subscription failed:', error);
        }
      }

      mutation.mutate(params, {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: ['program-invitations'] });
          // The worker can finish between the pre-send subscription and this
          // HTTP response. In that race, the terminal handler already cleared
          // the UI state; do not put it back into an indefinite waiting state.
          if (terminalEventReceivedRef.current) {
            return;
          }
          if (didSubscribe) {
            outreachIdRef.current = data.outreachId;
            return;
          }

          // No realtime channel: nothing terminal will ever arrive, so stop waiting now.
          announceStillSending();
        },
        onError: clearSubscription,
      });
    },
    [
      announceStillSending,
      clearResultTimeout,
      clearSubscription,
      mutation,
      queryClient,
      releaseSubscription,
      t,
      user?.currentBrand?.id,
    ]
  );

  return {
    send,
    isSending: mutation.isPending || isAwaitingResult,
  };
}
